/**
 * PruefberichtPanel.tsx — P4-S4 the consolidated Kontrollen-Prüfbericht (steps 15-20).
 *
 * Presentational: three sections (Unterschriften, Zeichnungsregel, Vollstaendigkeit) + the overall
 * badge + the Broker decision controls. The decision controls render only for a caller with
 * `mandate.eingangskontrolle`; «Genehmigen» stays disabled until every required confirmation is
 * ticked. Every decision carries the run id of the report it was taken on. A signer without a
 * resolvable name shows a placeholder (a name-only blacklist viewer gets no report from the api).
 *
 * Brokermandat (P5 step 19): the Mandatsumfang section lists the stored items (each can be struck
 * with a reason) and the insurances the return added (pre-ticked «in Umfang aufnehmen»); when the
 * report says the scope needs the broker's confirmation, «Genehmigen» also waits for «Mandatsumfang
 * bestätigt» and carries the scope decision. Whether a confirmation is needed is the api's answer.
 *
 * No PII decisions here: the api is the hard line; this only decides what to offer.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MANDATE_SCOPE_EXCLUDE_REASON,
  MANDATE_SCOPE_EXCLUDE_REASON_INPUT,
  SCOPE_EXCLUDE_REASON_LABEL,
  SCOPE_RESULT_LABEL,
  SIGNATURE_RESULT_LABEL,
  controlReviewReasonLabel,
  scopeItemLabel,
} from '@brokly/shared';
import type {
  CompletenessColour,
  CompletenessFinding,
  ControlOverall,
  SigningAuthorityResultCode,
  MandateScopeExcludeReason,
  PruefberichtDto,
  PruefberichtEntscheidBody,
} from '@brokly/shared';

const OVERALL_LABEL: Record<ControlOverall, string> = {
  auto_definitiv: 'Automatisch definitiv',
  review: 'Prüfung nötig',
};

const COLOUR_LABEL: Record<CompletenessColour, string> = {
  gruen: 'Grün',
  gelb: 'Gelb',
  rot: 'Rot',
  nicht_anwendbar: 'Nicht anwendbar (Mandatsumfang)',
};

const AUTHORITY_LABEL: Record<SigningAuthorityResultCode, string> = {
  erfuellt: 'erfüllt',
  nicht_erfuellt: 'nicht erfüllt',
  nicht_verifiziert: 'nicht prüfbar',
  nicht_anwendbar: 'nicht anwendbar',
  bestaetigt: 'bestätigt',
};

const FINDING_LABEL: Record<CompletenessFinding['code'], string> = {
  keine_angaben: 'Keine Angaben zu bestehenden Versicherungen',
  angabe_ohne_dokument: 'Angabe ohne Police oder Beleg',
  mindestfelder_fehlen: 'Policennummer oder Sparte fehlt',
  sollkatalog_fehlt: 'Grundversicherung nicht angegeben',
  versicherungsnehmer_abweichend: 'Versicherungsnehmer weicht ab',
  seiten_unvollstaendig: 'Seiten unvollständig',
};

/**
 * A signature image loaded with the session (an <img src> carries no credentials): the loader
 * returns the bytes, the image shows them through an object URL that is revoked on unmount.
 */
function SessionImage({ alt, queryKey, load }: { alt: string; queryKey: readonly unknown[]; load: () => Promise<Blob> }) {
  // A signature is personal data: never kept in the cache once the image is gone.
  const { data } = useQuery({ queryKey, queryFn: load, gcTime: 0 });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!data) return undefined;
    const next = URL.createObjectURL(data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [data]);
  return url ? <img alt={alt} src={url} className="h-12 border" /> : <span role="img" aria-label={alt} className="inline-block h-12 w-24 border" />;
}

const NAME_PLACEHOLDER = 'Name verborgen';

/** «1-2», «1, 3»: the pages a code was read on. */
function formatPages(pages: number[]): string {
  if (pages.length === 0) return '';
  const sorted = [...pages].sort((a, b) => a - b);
  const contiguous = sorted.every((p, i) => i === 0 || p === sorted[i - 1]! + 1);
  return contiguous && sorted.length > 1 ? `${sorted[0]}-${sorted[sorted.length - 1]}` : sorted.join(', ');
}

/** P4-FU3: why the returned document is the mandate, in one line. */
function evidenceLabel(doc: NonNullable<PruefberichtDto['returned_document']>): string {
  const pages = formatPages(doc.code_pages);
  const onPages = pages ? ` auf Seite ${pages}` : '';
  switch (doc.mandate_evidence) {
    case 'qr_image':
      return `Zuordnung: QR${onPages}`;
    case 'text_token':
      return `Zuordnung: Code im Text${onPages}`;
    case 'text_vorgangs_id':
      return `Zuordnung: Vorgangs-ID im Text${onPages}`;
    case 'scan_hint':
      return 'Zuordnung: Barcode des Scan-Centers';
    case 'manual':
      return 'Zuordnung: manuell markiert';
    case 'delivery':
      return doc.code_source === 'qr_image' || doc.code_source === 'text_token'
        ? `Portal-Zustellung, Code${onPages}`
        : 'Portal-Zustellung, kein Code gelesen';
    default:
      return 'Zuordnung: vor dem Code-Lesen erfasst';
  }
}

export interface PruefberichtPanelProps {
  report: PruefberichtDto;
  can: (key: string) => boolean;
  /** Loads a signature image with the session (the Gegenüberstellung). */
  loadSignatureImage?: (personId: string, which: 'reference' | 'returned') => Promise<Blob>;
  onDecide?: (body: PruefberichtEntscheidBody) => void;
}

export function PruefberichtPanel({ report, can, loadSignatureImage, onDecide }: PruefberichtPanelProps) {
  // Only an undecided review run takes a decision; the api answers 409 for anything else.
  const canDecide =
    can('mandate.eingangskontrolle') && report.run.overall === 'review' && report.run.decision == null;
  const required = report.required_confirmations;
  const [ticked, setTicked] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => setTicked((t) => ({ ...t, [key]: !t[key] }));

  // Brokermandat: the scope decision (the additions start ticked, nothing struck).
  const scope = report.scope ?? null;
  const [scopeConfirmed, setScopeConfirmed] = useState(false);
  const [additions, setAdditions] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((scope?.additions ?? []).map((a) => [a.relationshipId, true])),
  );
  const [struck, setStruck] = useState<Record<string, MandateScopeExcludeReason | null>>({});
  const scopeBlocks = scope !== null && scope.confirmationRequired && !scopeConfirmed;
  const allTicked = required.every((r) => ticked[r]) && !scopeBlocks;

  const nameOf = (displayName: string | null): string =>
    report.names_restricted || displayName === null ? NAME_PLACEHOLDER : displayName;
  const [confirmCancel, setConfirmCancel] = useState(false);

  /** A confirmation the decision needs, in words with the signer's name (never the code). */
  function confirmationLabel(code: string): string {
    if (code === 'signing_authority') return 'Zeichnungsberechtigung bestätigen (Vollmacht geprüft)';
    const [kind, personId] = code.split(':');
    const signer = report.signatures.find((x) => x.person_id === personId);
    const who = nameOf(signer?.display_name ?? null);
    return kind === 'reference' ? `Neue Referenzunterschrift von ${who} bestätigen` : `Unterschrift von ${who} bestätigen`;
  }

  function buildBody(decision: PruefberichtEntscheidBody['decision']): PruefberichtEntscheidBody {
    const confirm_signatures: string[] = [];
    const confirm_references: string[] = [];
    let confirm_signing_authority = false;
    for (const r of required) {
      if (!ticked[r]) continue;
      if (r.startsWith('signature:')) confirm_signatures.push(r.slice('signature:'.length));
      else if (r.startsWith('reference:')) confirm_references.push(r.slice('reference:'.length));
      else if (r === 'signing_authority') confirm_signing_authority = true;
    }
    // The run id lets the api refuse a decision on a report a newer return has replaced (409).
    const body: PruefberichtEntscheidBody = { decision, run_id: report.run.id, confirm_signatures, confirm_references, confirm_signing_authority };
    if (decision === 'genehmigen' && scope !== null && scopeConfirmed) {
      body.scope = {
        confirmed: true,
        addRelationshipIds: scope.additions.filter((a) => additions[a.relationshipId]).map((a) => a.relationshipId),
        excludeItemIds: Object.entries(struck)
          .filter((e): e is [string, MandateScopeExcludeReason] => e[1] !== null)
          .map(([id, reason]) => ({ id, reason })),
      };
    }
    return body;
  }

  return (
    <section className="flex flex-col gap-4 rounded-lg border bg-card p-4" aria-label="Prüfbericht" data-testid="pruefbericht-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Prüfbericht (Kontrollen &amp; Entscheid)</h3>
        <span
          data-testid="pruefbericht-overall"
          data-overall={report.run.overall}
          className={`rounded-md px-2 py-1 text-xs font-medium ${report.run.overall === 'review' ? 'bg-orange-100 text-orange-900' : 'bg-emerald-100 text-emerald-900'}`}
        >
          {OVERALL_LABEL[report.run.overall]}
        </span>
      </div>

      {(report.run.reasons ?? []).length > 0 ? (
        <ul data-testid="pruefbericht-reasons" className="list-disc pl-5 text-sm text-foreground">
          {(report.run.reasons ?? []).map((code) => (
            <li key={code}>{controlReviewReasonLabel(code)}</li>
          ))}
        </ul>
      ) : null}

      {/* Section 1: Unterschriften */}
      <div data-testid="section-signatures">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">Unterschriften</h4>
        <ul className="mt-1 flex flex-col gap-2">
          {report.signatures.map((s) => {
            const showImages = s.result === 'unsicher' || s.result === 'befund';
            return (
              <li key={s.field_ref} data-testid={`sig-${s.person_id ?? s.field_ref}`} className="text-sm">
                <span className="font-medium">{nameOf(s.display_name)}</span>
                {' - '}
                <span data-testid={`sig-result-${s.field_ref}`} data-result={s.result}>{SIGNATURE_RESULT_LABEL[s.result]}</span>
                {s.score != null ? <span className="text-muted-foreground"> (Score {s.score})</span> : null}
                <span className="text-xs text-muted-foreground">
                  {' '}Schwellwert {report.run.thresholds.auto} / Untergrenze {report.run.thresholds.uncertain}
                </span>
                {showImages && loadSignatureImage && s.person_id ? (
                  <div className="mt-1 flex gap-2" data-testid={`sig-images-${s.person_id}`}>
                    <SessionImage
                      alt="Referenz-Unterschrift"
                      queryKey={['pruefbericht-signature', report.run.id, s.person_id, 'reference']}
                      load={() => loadSignatureImage(s.person_id!, 'reference')}
                    />
                    <SessionImage
                      alt="Eingereichte Unterschrift"
                      queryKey={['pruefbericht-signature', report.run.id, s.person_id, 'returned']}
                      load={() => loadSignatureImage(s.person_id!, 'returned')}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Section 2 — Zeichnungsregel */}
      <div data-testid="section-signing-authority">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">Zeichnungsberechtigung</h4>
        <p className="mt-1 text-sm">
          <span data-testid="authority-result" data-result={report.signing_authority.result}>{AUTHORITY_LABEL[report.signing_authority.result]}</span>
          {report.signing_authority.rule ? <span className="text-muted-foreground"> ({report.signing_authority.rule})</span> : null}
          {report.signing_authority.register_fetched_at ? (
            <span className="text-xs text-muted-foreground"> Abrufdatum {report.signing_authority.register_fetched_at}</span>
          ) : null}
        </p>
        <ul className="text-xs text-muted-foreground">
          {report.signing_authority.matches.map((m, i) => (
            <li key={m.person_id ?? `register-${i}`}>
              {nameOf(m.display_name)} - {m.matched ? 'im Register' : 'nicht im Register'}
              {m.signature ? ` (${m.signature})` : ''}
            </li>
          ))}
        </ul>
      </div>

      {/* P4-FU3: why the checked document is the signed mandate */}
      {report.returned_document ? (
        <div data-testid="section-returned-document">
          <p className="text-xs text-muted-foreground" data-testid="returned-document-evidence">
            {evidenceLabel(report.returned_document)}
          </p>
          {report.returned_document.code_vorgang_id && report.returned_document.code_vorgang_id !== report.run.vorgang_id ? (
            <p className="text-xs font-medium text-destructive" role="alert" data-testid="returned-document-warning">
              Code eines anderen Vorgangs
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Section 3 — Vollstaendigkeit */}
      <div data-testid="section-completeness">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">Vollständigkeit</h4>
        <p className="mt-1 text-sm" data-testid="completeness-colour" data-colour={report.completeness.colour}>
          {COLOUR_LABEL[report.completeness.colour]}
        </p>
        <ul className="text-xs text-muted-foreground">
          {report.completeness.findings.map((f, i) => (
            <li key={`${f.code}-${i}`}>{FINDING_LABEL[f.code]}</li>
          ))}
        </ul>
      </div>

      {/* Section 4: Mandatsumfang (Brokermandat) */}
      {scope !== null ? (
        <div data-testid="section-scope" data-result={scope.result}>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">Mandatsumfang</h4>
          <p className="mt-1 text-sm">{SCOPE_RESULT_LABEL[scope.result]}</p>
          <ul className="mt-1 flex flex-col gap-1 text-sm">
            {scope.items.map((item) => {
              const isStruck = struck[item.id] !== undefined && struck[item.id] !== null;
              return (
                <li key={item.id} data-testid={`scope-item-${item.id}`} className="flex flex-wrap items-center gap-2">
                  <span className={item.included && !isStruck ? '' : 'text-muted-foreground line-through'}>{scopeItemLabel(item)}</span>
                  {!item.included && item.excludedReason ? (
                    <span className="text-xs text-muted-foreground">({SCOPE_EXCLUDE_REASON_LABEL[item.excludedReason]})</span>
                  ) : null}
                  {canDecide && item.included ? (
                    <>
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          aria-label="streichen"
                          checked={isStruck}
                          onChange={(e) =>
                            setStruck((cur) => ({ ...cur, [item.id]: e.target.checked ? MANDATE_SCOPE_EXCLUDE_REASON.KUNDE_GESTRICHEN : null }))
                          }
                        />
                        streichen
                      </label>
                      <select
                        aria-label="Grund"
                        className="rounded-md border px-1 py-0.5 text-xs"
                        value={struck[item.id] ?? MANDATE_SCOPE_EXCLUDE_REASON.KUNDE_GESTRICHEN}
                        onChange={(e) => setStruck((cur) => ({ ...cur, [item.id]: e.target.value as MandateScopeExcludeReason }))}
                      >
                        {MANDATE_SCOPE_EXCLUDE_REASON_INPUT.map((r) => (
                          <option key={r} value={r}>{SCOPE_EXCLUDE_REASON_LABEL[r]}</option>
                        ))}
                      </select>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {scope.additions.length > 0 ? (
            <div className="mt-2">
              <p className="text-xs font-semibold text-muted-foreground">Mit dem Rücklauf neu angegeben</p>
              <ul className="flex flex-col gap-1 text-sm">
                {scope.additions.map((a) => (
                  <li key={a.relationshipId} data-testid={`scope-addition-${a.relationshipId}`}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        disabled={!canDecide}
                        checked={!!additions[a.relationshipId]}
                        onChange={(e) => setAdditions((cur) => ({ ...cur, [a.relationshipId]: e.target.checked }))}
                      />
                      {scopeItemLabel(a)} · in Umfang aufnehmen
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {canDecide ? (
            <label className="mt-2 flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                aria-label="Mandatsumfang bestätigt"
                checked={scopeConfirmed}
                onChange={(e) => setScopeConfirmed(e.target.checked)}
              />
              Mandatsumfang bestätigt
            </label>
          ) : null}
        </div>
      ) : null}

      {/* Decision controls — gated by the permission */}
      {canDecide ? (
        <div data-testid="decision-controls" className="flex flex-col gap-2 border-t pt-3">
          {required.length > 0 ? (
            <fieldset className="flex flex-col gap-1">
              <legend className="text-xs font-semibold text-muted-foreground">Bestätigungen</legend>
              {required.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`confirm-${r}`}
                    checked={!!ticked[r]}
                    onChange={() => toggle(r)}
                  />
                  {confirmationLabel(r)}
                </label>
              ))}
            </fieldset>
          ) : null}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="btn-genehmigen"
              disabled={!allTicked}
              onClick={() => onDecide?.(buildBody('genehmigen'))}
              className="rounded-md bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-40"
            >
              Genehmigen
            </button>
            <button
              type="button"
              data-testid="btn-nachforderung"
              onClick={() => onDecide?.(buildBody('nachforderung'))}
              className="rounded-md bg-orange-500 px-3 py-1 text-sm text-white"
            >
              Nachforderung
            </button>
            <button
              type="button"
              data-testid="btn-abbrechen"
              onClick={() => setConfirmCancel(true)}
              className="rounded-md border px-3 py-1 text-sm"
            >
              Abbrechen
            </button>
          </div>
          {confirmCancel ? (
            <div role="alertdialog" aria-label="Vorgang abbrechen" className="flex flex-wrap items-center gap-2 rounded-md border border-destructive p-2 text-sm">
              <span>Den Vorgang und das Mandat wirklich abbrechen? Das kann nicht rückgängig gemacht werden.</span>
              <button
                type="button"
                data-testid="btn-abbrechen-bestaetigen"
                onClick={() => {
                  setConfirmCancel(false);
                  onDecide?.(buildBody('abbrechen'));
                }}
                className="rounded-md bg-destructive px-3 py-1 text-white"
              >
                Ja, abbrechen
              </button>
              <button type="button" onClick={() => setConfirmCancel(false)} className="rounded-md border px-3 py-1">
                Nein
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
